import ArchiveIcon from '@mui/icons-material/Inventory2Outlined';
import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  archiveDailySessionNoteAction,
  getArchivedDocumentsAction,
  resetArchiveDailySessionNote,
} from 'src/redux/actions/dailySessionNotes';
import ArchiveNoteConfirmation from '../dailySessionNotes/archive-note-confirmation';
import { BootstrapDialog } from '../common-modals/BootstrapDialog';
import { formatDateOfService } from './format-date-of-service';

const isDailySessionNote = (document: any) =>
  Boolean(document?.dailySessionNoteUuid) &&
  Boolean(document?.formType?.includes('DAILY_SOAP_NOTE'));

/**
 * BOPS-151: whether this note is already archived.
 *
 * Archiving runs the existing DSN delete underneath, so whether an archived note is still
 * returned by the completed-documents list at all is the backend's call. This reads the flag
 * defensively — if the list stops returning archived notes the predicate simply never fires
 * and the Archived Documents section is the only surface, which is still correct.
 *
 * BOPS-151: archiving now HARD-DELETES the note and its form submission, so an archived note is
 * never returned by either list and this predicate reads false for every row it sees. It is kept so
 * the lists stay correct if that ever changes; the Archived Documents section is the real surface.
 * The old `complianceHold` fallback is gone — no endpoint ever emitted that field.
 */
export const isArchivedNote = (document: any) => document?.archived === true;

/**
 * BOPS-151 — an archived note is read-only: View Document is the only action it keeps, Delete
 * included, because the note is already out of billing and the document has to stay reachable.
 *
 * Applied to a finished menu rather than item by item so the two document lists cannot drift
 * apart, and so an action added to either menu later inherits the rule instead of forgetting it.
 * The kept item is named by label because that is what the caller already writes; both callers
 * pass the same constant they used to build the item.
 */
export const applyArchivedReadOnly = <T extends { label: string; hidden?: boolean }>(
  items: T[],
  isReadOnly: boolean,
  keptLabel: string
): T[] =>
  isReadOnly
    ? items.map((item) => (item.label === keptLabel ? item : { ...item, hidden: true }))
    : items;

/**
 * BOPS-151 — how an archived document's title reads: struck through and greyed. Shared by the two
 * document lists and the Archived Documents table so the three cannot drift apart.
 */
export const archivedTitleSx = { textDecoration: 'line-through', color: '#6C737F' };

export const ArchivedBadge = () => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '2px',
      marginLeft: 1,
      padding: '4px 10px 4px 6px',
      borderRadius: '100px',
      backgroundColor: '#eceef2',
      color: '#5b6172',
      fontSize: '12px',
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}
  >
    <ArchiveIcon sx={{ fontSize: '16px', color: '#5b6172' }} />
    Archived
  </Box>
);

/**
 * BOPS-151 / BOPS-173 / BOPS-174 — the Archive gate.
 *
 * Owns the whole decision in one place, the way useHoldFromBilling does, so the Episode of
 * Care menu, the Daily Notes and Billing bar (BOPS-175) and the Archived badge all gate
 * identically instead of each re-deriving it.
 *
 * Archive is hidden — never greyed — when any of these is false:
 *   - the BOPS-174 enterprise toggle is on (=== true: an unresolved Split treatment is null
 *     and a control treatment is false, and both have to read as OFF),
 *   - the user holds the BOPS-173 permission,
 *   - the note produced a PDF (keeping it on the record is the whole point of an archive),
 *   - the note's month is not closed out.
 *
 * @param episodeOfCareId refetch the Archived Documents list for this episode once an
 *   archive lands, so the note leaves the Documents list and appears below in one step.
 * @param onArchived fired once the archive lands. Both Episode of Care lists pass their own
 *   refetch here, the same one they already run after a delete: archiving runs the DSN delete
 *   underneath, so the row and the list's total count are both stale the moment it succeeds.
 *   The reducers' in-place patch only has to carry the row until that response arrives.
 */
export const useArchiveNote = (options?: {
  episodeOfCareId?: string;
  onArchived?: () => void;
  /**
   * BOPS-175 — the patient whose note is being archived, for callers that don't have one in
   * the store. The Episode of Care has already loaded `getSinglePatientReducer` by the time
   * either list renders; the Daily Notes and Billing page never loads it at all, so without
   * this its confirmation dialog would name a blank patient.
   */
  patientName?: string;
}) => {
  const { episodeOfCareId, onArchived, patientName: patientNameOverride } = options || {};
  const dispatch = useDispatch();
  const [documentPendingArchive, setDocumentPendingArchive] = useState<any>(null);

  const archiveState = useSelector((state: any) => state?.archiveDailySessionNoteReducer);
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const assignedFacilities = useSelector((state: any) => state?.getFacilityDropDownReducer);

  const facilityName =
    assignedFacilities?.response?.data?.data?.find(
      (facility: any) => facility?.uuid === localStorage.getItem('facilityId')
    )?.name ?? '';

  const canArchive = (document: any) => document?.archiveButton !== false;

  useEffect(() => {
    if (documentPendingArchive && !canArchive(documentPendingArchive)) {
      setDocumentPendingArchive(null);
    }
  }, [documentPendingArchive]);

  useEffect(() => {
    if (archiveState?.isSuccess && documentPendingArchive) {
      setDocumentPendingArchive(null);
      dispatch(resetArchiveDailySessionNote());
      if (episodeOfCareId) dispatch(getArchivedDocumentsAction({ episodeOfCareId }));
      onArchived?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archiveState?.isSuccess]);

  const confirmArchive = (reason: string) => {
    if (!canArchive(documentPendingArchive) || !reason?.trim()) return;
    dispatch(
      archiveDailySessionNoteAction({
        dsnUuid: documentPendingArchive?.dailySessionNoteUuid,
        reason: reason.trim(),
      })
    );
  };

  const closeDialog = () => {
    setDocumentPendingArchive(null);
    //  Clear a refusal so the next note the user opens does not inherit the last one's error.
    if (archiveState?.isError) dispatch(resetArchiveDailySessionNote());
  };

  const patientName =
    patientNameOverride?.trim() ||
    [patientData?.lastName, patientData?.firstName].filter(Boolean).join(', ');

  return {
    canArchive,
    //  BOPS-173 gates the badge as well as the action: without the permission a user must
    //  not learn that a note was archived. Same shape as isHeldFromBilling.
    isArchived: (document: any) => isArchivedNote(document),
    //  BOPS-151 — whether the row's other actions have to come off. Deliberately NOT gated on
    //  the permission the way the badge is: the underlying delete has already run, so Delete,
    //  Undo Signature and the rest would fail for anyone. Hiding them is a property of the
    //  note, not of who is looking at it.
    isReadOnly: (document: any) => isArchivedNote(document),
    /**
     * Archivable but for the closed period — i.e. the action should be OFFERED and then refuse
     * with an explanation, rather than disappear.
     *
     * The Daily Notes and Billing page uses this: there the note is the whole screen, so an
     * Archive button that is simply absent reads as a bug or a missing permission, and the user
     * has nothing to click to find out why. It already answers Edit and Start Note the same way,
     * through the closed-period dialog. The Episode of Care menus deliberately do NOT use it —
     * there the action lives in a row menu among others and hiding it is still the right call.
     */
    requestArchive: (document: any) => {
      if (!canArchive(document)) return;
      if (archiveState?.isError) dispatch(resetArchiveDailySessionNote());
      setDocumentPendingArchive(document);
    },
    archiveConfirmationDialog: (
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '520px' } }}
        onClose={closeDialog}
        aria-labelledby="archive-note-dialog-title"
        open={Boolean(documentPendingArchive)}
      >
        <ArchiveNoteConfirmation
          close={closeDialog}
          confirm={confirmArchive}
          patientName={patientName || patientData?.name || ''}
          dateOfService={formatDateOfService(documentPendingArchive?.dateOfService, 'MMMM d, yyyy')}
          therapistName={documentPendingArchive?.signatureContext?.therapist?.name || ''}
          facility={facilityName}
          loading={archiveState?.loading}
          errorMessage={
            archiveState?.isError
              ? archiveState?.response?.message ||
                'This note could not be archived. Please try again.'
              : undefined
          }
        />
      </BootstrapDialog>
    ),
  };
};
